import { EContractStatus, ECreditApplicationStatus, EOriginSystemId } from '@if/core/types'
import { Action, ActionList, Icon } from '@if/mui'
import { Chip, Stack, Tooltip } from '@mui/material'
import { useUnreadCreditApplicationIds } from '@src/data/api/chat-message/chat-message-api'
import { usePostStatus } from '@src/data/api/credit-api/credit-api'
import { getCanEdit } from '@src/data/creditapp-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Ban, CircleHelp, MessageSquare, RotateCcw } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCancelReasonsDialog } from './CancelReasonsDialog'

interface Props {
  creditApp: CreditApplication
}

const ViewApplicationActions = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const hasUnread = useUnreadCreditApplicationIds().has(creditApp.id)
  const canEdit = getCanEdit(creditApp.editLocked)
  const { mutateAsync: postStatus } = usePostStatus()
  const cancelDialog = useCancelReasonsDialog(creditApp)

  const isMerchantDashboardOrigin = creditApp.originSystemId === EOriginSystemId.MerchantDashboard
  const isReactivate = creditApp.status === ECreditApplicationStatus.Cancelled
  const limitedAccessReason =
    creditApp.originSystemId === EOriginSystemId.Backoffice
      ? t('viewCreditApplication.limitedAccessBackOffice')
      : t('viewCreditApplication.limitedAccessSubscription')

  const handleCancelOrReactivate = useCallback(async () => {
    if (creditApp.status === ECreditApplicationStatus.Active) {
      void cancelDialog.open()
    } else {
      await postStatus({
        id: creditApp.id,
        status: ECreditApplicationStatus.Active,
        financingProgramId: creditApp.financingProgramId,
      })
    }
  }, [creditApp, postStatus, cancelDialog])

  const actions: Action[] = useMemo(() => {
    const list: Action[] = [
      {
        icon: MessageSquare,
        label: hasUnread ? t('common.unreadMessages') : t('common.messages'),
        tone: hasUnread ? 'warning' : 'default',
        display: 'button',
        href: `/chat-dashboard/${creditApp.financingProgramId}/${creditApp.id}?from=application`,
      },
    ]
    if (canEdit && isMerchantDashboardOrigin) {
      list.push({
        icon: isReactivate ? RotateCcw : Ban,
        label: isReactivate ? t('viewCreditApplication.reactivate') : t('common.cancel'),
        tone: isReactivate ? 'default' : 'destructive',
        disabled: creditApp.contract?.status === EContractStatus.Completed,
        display: 'button',
        onClick: () => void handleCancelOrReactivate(),
      })
    }
    return list
  }, [hasUnread, canEdit, isMerchantDashboardOrigin, isReactivate, creditApp, handleCancelOrReactivate, t])

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
      {!isMerchantDashboardOrigin && (
        <Tooltip title={limitedAccessReason}>
          <Chip
            icon={<Icon icon={CircleHelp} size="sm" />}
            label={t('viewCreditApplication.limitedAccess')}
            color="info"
            tabIndex={0}
            aria-label={`${t('viewCreditApplication.limitedAccess')} — ${limitedAccessReason}`}
            sx={{ cursor: 'help' }}
          />
        </Tooltip>
      )}
      {creditApp.isInvisalign && <Chip label={t('editCreditApplication.invisalign.indicator')} color="info" />}
      <ActionList actions={actions} variant="button" menuLabel={t('viewCreditApplication.actions')} />
    </Stack>
  )
}

export default ViewApplicationActions
